/**
 * Type. One scale, two densities.
 *
 * The POS is read at arm's length across a counter and the dashboard is read at a desk, so the
 * two apps set a different root size (`--pos-root-size`) and inherit the same rem-based scale.
 * That keeps one type system rather than two, and means a heading is proportionally a heading in
 * both places.
 */

/**
 * Plus Jakarta Sans: geometric enough to have the One UI character the brief asks for, but with
 * open apertures and a tall x-height that hold up at 14px on a cheap Android panel. It is also
 * variable and OFL-licensed, so the resale requirement stays clean.
 *
 * JetBrains Mono carries IMEIs, SKUs and serials only. A 15-digit IMEI read aloud from a
 * proportional font is where transcription errors come from — fixed advance width makes the
 * digits countable.
 */
export const fontFamily = {
  sans: "'Plus Jakarta Sans', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  mono: "'JetBrains Mono', ui-monospace, 'SF Mono', 'Cascadia Mono', monospace",
} as const;

/** Root font size per app. The scale below is relative to this. */
export const rootSize = {
  /** Counter distance, gloved or hurried thumbs. */
  pos: '17px',
  /** Desk distance, dense tables. */
  dashboard: '15px',
} as const;

export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
} as const;

export type TypeStep = {
  readonly size: string;
  readonly lineHeight: string;
  readonly weight: number;
  readonly tracking: string;
};

/**
 * A 1.2 minor-third scale, tightened at the display end. Tracking goes negative as size grows —
 * large geometric type set at default spacing looks loose and amateur.
 */
export const typeScale = {
  '2xs': { size: '0.6875rem', lineHeight: '1rem', weight: 500, tracking: '0.02em' },
  xs: { size: '0.75rem', lineHeight: '1.125rem', weight: 500, tracking: '0.01em' },
  sm: { size: '0.875rem', lineHeight: '1.3125rem', weight: 400, tracking: '0em' },
  base: { size: '1rem', lineHeight: '1.5rem', weight: 400, tracking: '0em' },
  lg: { size: '1.125rem', lineHeight: '1.625rem', weight: 500, tracking: '-0.005em' },
  xl: { size: '1.375rem', lineHeight: '1.875rem', weight: 600, tracking: '-0.01em' },
  '2xl': { size: '1.75rem', lineHeight: '2.25rem', weight: 600, tracking: '-0.015em' },
  '3xl': { size: '2.25rem', lineHeight: '2.625rem', weight: 700, tracking: '-0.02em' },
  '4xl': { size: '3rem', lineHeight: '3.25rem', weight: 700, tracking: '-0.025em' },
  '5xl': { size: '4rem', lineHeight: '4.25rem', weight: 800, tracking: '-0.03em' },
} as const satisfies Record<string, TypeStep>;

export type TypeScaleName = keyof typeof typeScale;

/**
 * Named roles, so a component asks for "the amount on the cart line" rather than picking a step.
 * Money and identifiers are the two that matter: both need tabular figures, because a column of
 * prices where the digits do not line up is unscannable.
 */
export const typeRole = {
  displayHero: '5xl',
  displayLarge: '4xl',
  display: '3xl',
  headingLarge: '2xl',
  heading: 'xl',
  subheading: 'lg',
  body: 'base',
  bodySmall: 'sm',
  caption: 'xs',
  overline: '2xs',
  /** Cart totals, price entry, the amount on the payment screen. */
  amountLarge: '3xl',
  amount: 'lg',
  /** IMEI, SKU, serial, receipt number. */
  identifier: 'sm',
} as const satisfies Record<string, TypeScaleName>;

/**
 * Applied to money and identifiers. `tabular-nums` fixes digit advance width; `slashed-zero`
 * keeps a zero apart from an O in a serial that is being typed back into a phone.
 */
export const numericFeatures = {
  money: 'tabular-nums',
  identifier: 'tabular-nums slashed-zero',
} as const;
